import { prefix } from "../../design-tokens/meta";
import { matchers } from "../../js/validate-props";
import { isHTMLElement } from "../../js/utilities";
import { actions, events } from "../../js/state-machine";

export const componentName = "Accordion";

export const componentClasses = Object.freeze({
    accordion: `${prefix}accordion`,
    controls: `${prefix}accordion__control`,
    expandAll: `${prefix}accordion__control__expand`,
    collapseAll: `${prefix}accordion__control__collapse`,
    screenReader: `${prefix}accordion__sr`,
});

export const defaultProps = Object.freeze({
    independent: true,
    srExpandAll: "All items expanded",
    srCollapseAll: "All items collapsed",
    srExpandItem: "Item expanded",
    srCollapseItem: "Item collapsed",
});

export const schema = Object.freeze({
    independent: matchers.boolean,
    srExpandAll: matchers.minChars(1),
    srCollapseAll: matchers.minChars(1),
    srExpandItem: matchers.minChars(1),
    srCollapseItem: matchers.minChars(1),
});

export const eventMap = Object.freeze({
    //Mapping wrapper actions with events
    [actions.collapseAll().type]: events.collapsedAll(componentName),
    [actions.expandAll().type]: events.expandedAll(componentName),
    //Mapping event form accordion item
    collapsing: events.collapsing(componentName),
    expanding: events.expanding(componentName),
    collapsed: events.collapsed(componentName),
    expanded: events.expanded(componentName),
});

export const views = Object.freeze({
    controls: {
        active: {
            attrs: {
                "aria-disabled": "false",
                disabled: null,
            },
        },
        inactive: {
            attrs: {
                "aria-disabled": "true",
                disabled: "disabled",
            },
        },
    },
});

/**
 * Loops through accordionItems to focus first not inactive item.
 * @param {Array} accordionItems list of AccordionItem instances;
 * @return {void}
 */
export const setFocusFirstAvailableItemOnList = (accordionItems) => {
    while (accordionItems.length) {
        const nextItem = accordionItems.shift();
        if (nextItem && !nextItem.isInactive && nextItem.buttonElement) {
            nextItem.buttonElement.focus();
            break;
        }
    }
};

/**
 * Creates a new list from **accordionItems** where the
 * first item will be **index** and the last item **index - 1**
 * @param {Number} index the current index you want to find the next
 * @param {Array} accordionItems;
 * @return {Array}
 */
export const forwardListFromIndex = (index, accordionItems = []) => {
    if (index >= accordionItems.length || index < 0) return accordionItems;
    // Bump given index
    // send it to the beginning in case it's the last
    index = index >= accordionItems.length ? 0 : index;
    if (index === 0) {
        return accordionItems.concat([]);
    }
    const left = accordionItems.slice(0, index);
    const right = accordionItems.slice(index, accordionItems.length);
    return right.concat(left);
};

/**
 * Creates a new list from **accordionItems** where the
 * first item will be **index** and the last item **index + 1**
 * @param {Number} index the current index you want to find the next
 * @param {Array} accordionItems;
 * @return {Array}
 */
export const backwardListFromIndex = (index, accordionItems = []) => {
    if (index >= accordionItems.length || index < 0) return accordionItems;
    // Dec given index or use the end of the list
    index = Math.min(accordionItems.length - 1, index);
    if (index === accordionItems.length - 1) {
        return accordionItems.concat([]).reverse();
    }
    const left = accordionItems.slice(0, index + 1);
    const right = accordionItems.slice(index + 1, accordionItems.length);
    return left.reverse().concat(right.reverse());
};

/**
 * Find the accordion-item instance on accordionItems list
 * based on **index** which can be a number or an htmlElement
 * Returns -1 when index is not found on accordionItems
 * @param {*} index Number or HTML Element
 * @param {Array} accordionItems list of AccordionItem instances
 * @returns {Number}
 */
export const getIndexFromParam = (index, accordionItems) => {
    if (typeof index === "number") {
        return index >= 0 && index < accordionItems.length ? index : -1;
    }
    if (isHTMLElement(index)) {
        index = accordionItems.reduce((acc, item) => {
            if (item.element === index) {
                acc = item.index;
            }
            return acc;
        }, -1);
        if (index !== -1) return index;
    }
    return -1;
};
